import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useCallback, useContext, useMemo, useState } from "react";
import IncidentPDFTemplate from "../../../components/PDF/Reporting/incident-report/IncidentPDFTemplate";
import ReportingFormDrawerV2 from "../../../components/Reporting/ReportingFormDrawerV2";
import { ReportStreamColumn } from "../../../components/Reporting/stream/ReportStreamList";
import ReportStreamPage from "../../../components/Reporting/stream/ReportStreamPage";
import {
	ReportStreamItemsSummary,
	ReportStreamMeta,
	ReportStreamNumberCell,
	ReportStreamRowMenu,
	ReportStreamStatusChip,
	ReportStreamTitle,
} from "../../../components/Reporting/stream/ReportStreamParts";
import { HomeContext } from "../../../containers/Home";
import {
	ReportStreamConfig,
	useReportStream,
} from "../../../hooks/useReportStream";
import {
	ReportItem,
	ReportStreamAssets,
} from "../../../hooks/useReportStreamFilter";
import {
	useReportPhases,
	useReportStreamNames,
	useTenantName,
} from "../../../hooks/useReportStreamLookups";
import { getMtoCategoriesList } from "../../../services/api-server/mto_categories";
import { fetchReportingHubReports } from "../../../services/reporting-hub/fetchReports";
import { useTenantStore } from "../../../stores/tenantStore";
import { formatDateDdMmmYyyy } from "../../../utils/date_formatter";
import {
	addCauseCategoryNames,
	getIncidentExportData,
	getIncidentTypeLabel,
} from "../../../utils/reporting/incidentReport";
import {
	getIncidentMeta,
	getIncidentSearchTexts,
	getIncidentType,
	StreamIncident,
} from "../../../utils/reporting/incidentStream";
import { createIncidentLookupConfig } from "../../../utils/reporting/lookupConfigs/incident";
import { REPORT_ITEM_KINDS } from "../../../utils/reporting/reportItemKinds";
import {
	collectAuthorEmails,
	getDateValue,
	getItemsSummary,
	getReportAuthorName,
	getReportNumberValue,
	StreamPlaces,
} from "../../../utils/reporting/reportStream";
import {
	DEFAULT_REPORT_STATUSES,
	ReportStatusKey,
} from "../../../utils/reporting/reportStatusOptions";
import {
	collectReportExportEmails,
	fetchExportNames,
	getReportExportData,
	lookUpReportForExport,
} from "../../../utils/reporting/reportStreamExport";
import { handleExportReport } from "../../../utils/reports";

type IncidentRow = ReportItem &
	StreamIncident & {
		submitted_on?: string;
		is_rejected?: boolean;
		follow_up_actions?: unknown;
		causes?: unknown;
	};

// Incident reports never reach Approved - Open, so they have four figures.
const INCIDENT_STATUSES: ReportStatusKey[] = [
	"in_review",
	"in_progress",
	"final_review",
	"closed",
];

const INCIDENT_STREAM: ReportStreamConfig<IncidentRow> = {
	statuses: INCIDENT_STATUSES,
	defaultStatuses: INCIDENT_STATUSES.filter((key) =>
		DEFAULT_REPORT_STATUSES.includes(key)
	),
	hasInternalExternal: false,
	filters: [
		{
			key: "type",
			label: "Incident type",
			getValue: getIncidentType,
			getOptionLabel: getIncidentTypeLabel,
		},
	],
	sortValues: {
		number: (report) => getReportNumberValue(report.report_number),
		date: (report) => getDateValue(report.submitted_on),
	},
};

const NO_INCIDENTS: IncidentRow[] = [];

const getIncidentPath = (report: IncidentRow) =>
	`/reporting/incident-stream/${report._id}`;

// The tenant's cause categories, for the names in the PDF's causes table.
// Without them it still exports, as the incident page does.
async function fetchCauseCategories(operator: string | undefined) {
	if (!operator) return [];
	try {
		return await getMtoCategoriesList(operator);
	} catch (error) {
		// The message only: a request error carries the bearer token.
		console.error(
			"Failed to look up the cause categories for the incident's PDF:",
			error instanceof Error ? error.message : error
		);
		return [];
	}
}

// The incident report's PDF, as its page exports it: the page's lookups for
// this report alone, the names of everyone it names, and its causes'
// categories.
async function exportIncident(report: IncidentRow) {
	const operators = report.operator ? [report.operator] : [];
	const [lookedUp, emailToName, categories] = await Promise.all([
		lookUpReportForExport(report, createIncidentLookupConfig(operators)),
		fetchExportNames(collectReportExportEmails(report, "follow_up_actions")),
		fetchCauseCategories(report.operator),
	]);
	const withNames = getReportExportData(
		addCauseCategoryNames(lookedUp, categories),
		emailToName,
		"follow_up_actions"
	);
	await handleExportReport(
		getIncidentExportData(withNames),
		"incident",
		IncidentPDFTemplate
	);
}

function IncidentStream() {
	const { currentTenantKey }: any = useContext(HomeContext);
	const isMasterTenant = useTenantStore((state) => state.isMasterTenant);
	const getTenantName = useTenantName();
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);

	// The Reporting Hub fills and reads the same entry, and a saved report
	// is written into it (ReportingFormDrawerV2), so the rows stay as loaded.
	const { data, isPending, isError, isFetching, refetch } = useQuery({
		queryKey: ["reporting-hub-data", "incident_report"],
		queryFn: () =>
			fetchReportingHubReports(
				"incident_report",
				currentTenantKey,
				isMasterTenant
			),
		enabled: !!currentTenantKey,
		staleTime: 5 * 60 * 1000,
	});
	const incidents: IncidentRow[] | undefined = Array.isArray(data)
		? data
		: undefined;
	const rows = incidents ?? NO_INCIDENTS;

	// Only the listed authors are looked up by name, and only the listed
	// reports' phases.
	const authorEmails = useMemo(() => collectAuthorEmails(rows), [rows]);
	const authorNames = useReportStreamNames(
		["incident-stream-authors"],
		authorEmails
	);
	const phaseMap = useReportPhases(rows, currentTenantKey);

	const getSearchTexts = useCallback(
		(report: IncidentRow, assets: ReportStreamAssets) =>
			getIncidentSearchTexts(
				report,
				{ ...assets, phaseMap },
				authorNames,
				getTenantName
			),
		[phaseMap, authorNames, getTenantName]
	);

	const view = useReportStream(
		incidents,
		INCIDENT_STREAM,
		getSearchTexts,
		currentTenantKey,
		isMasterTenant
	);
	const places = useMemo<StreamPlaces>(
		() => ({ ...view.assets, phaseMap }),
		[view.assets, phaseMap]
	);

	const today = dayjs();
	const columns: ReportStreamColumn<IncidentRow>[] = [
		{
			key: "number",
			header: "Report no.",
			width: "140px",
			narrow: "number",
			sortKey: "number",
			render: (report) => (
				<ReportStreamNumberCell
					to={getIncidentPath(report)}
					reportNumber={report.report_number}
					operator={view.hasSeveralOperators ? report.operator : undefined}
				/>
			),
		},
		{
			key: "incident",
			header: "Incident report",
			width: "minmax(180px, 1fr)",
			narrow: "main",
			render: (report) => {
				const { tag, parts } = getIncidentMeta(report, places);
				return (
					<>
						<ReportStreamTitle title={report.title} />
						<ReportStreamMeta tag={tag} parts={parts} />
					</>
				);
			},
		},
		{
			key: "date",
			header: "Submitted",
			width: "104px",
			narrow: "detail",
			sortKey: "date",
			render: (report) => formatDateDdMmmYyyy(report.submitted_on),
		},
		{
			key: "author",
			header: "Author",
			width: "140px",
			narrow: "detail",
			render: (report) => (
				<span className="report-stream__ellipsis">
					{getReportAuthorName(report, authorNames)}
				</span>
			),
		},
		{
			key: "actions",
			header: "Actions",
			width: "148px",
			narrow: "full",
			render: (report) => (
				<ReportStreamItemsSummary
					kind={REPORT_ITEM_KINDS.follow_up_actions}
					summary={getItemsSummary(report.follow_up_actions, report, today)}
				/>
			),
		},
		{
			key: "status",
			header: "Status",
			width: "132px",
			narrow: "status",
			render: (report) => (
				<ReportStreamStatusChip
					status={report.status}
					openStatus={report.open_status}
				/>
			),
		},
	];

	return (
		<div className="generic-container overflow-auto">
			<ReportStreamPage
				title="Incident reports"
				subtitle={
					isMasterTenant
						? "Incident reports from every tenant."
						: "Your tenant's incident reports."
				}
				noun="incident report"
				plural="incident reports"
				newLabel="New incident report"
				onNew={() => setIsDrawerOpen(true)}
				emptyText={
					isMasterTenant
						? "Incident reports from every tenant will show here."
						: "Incident reports your tenant submits will show here."
				}
				searchPlaceholder="Search number, title, description, location, asset"
				view={view}
				isLoading={isPending}
				loadFailed={isError && !incidents}
				isRetrying={isFetching}
				onRetry={() => void refetch()}
				columns={columns}
				getRowKey={(report) => report._id}
				renderRowMenu={(report) => (
					<ReportStreamRowMenu
						reportNumber={report.report_number}
						path={getIncidentPath(report)}
						tenantKey={currentTenantKey}
						onExport={() => exportIncident(report)}
					/>
				)}
			/>
			<ReportingFormDrawerV2
				isOpen={isDrawerOpen}
				reportType="incident_report"
				onClose={() => setIsDrawerOpen(false)}
			/>
		</div>
	);
}

export default IncidentStream;
